import { gql, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import type { DocumentNode } from "graphql";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import type { PlatformBlockComponent } from "../../../types";
import { BlockFormHeader } from "../BlockFormHeader";
import { TextField } from "@/shared/components/Form/TextField";
import { AutocompleteField } from "@/shared/components/Form/AutocompleteField";
import { Switch } from "@/shared/components/Form/Switch";
import { ItemsPerRowField } from "../../ItemsPerRowField";

/**
 * `shared` не может импортировать конкретные partner/admin-квери напрямую
 * (см. границы слоёв в eslint.boundaries.mjs) — запрос типов коллекций
 * приходит через проп `queries.collectionTypes` (см.
 * partner/features/platformPages/model/schemas.ts). Этот noop-документ —
 * заглушка для useQuery на случай, если родитель квери не передал: `skip`
 * не даёт Apollo Client упасть на валидации `undefined`-документа.
 */
const NOOP_QUERY: DocumentNode = gql`
  query ContentsBlockCollectionTypesNoop {
    __typename
  }
`;

const viewValues = ["CAROUSEL", "GRID", "LIST"] as const;

const previewModeValues = ["HORIZONTAL", "VERTICAL"] as const;

const sortValues = ["LATEST", "OLDEST", "UPDATED", "MANUAL"] as const;

const statusValues = ["PENDING", "STARTED", "FINISHED"] as const;

const typeValues = ["VIDEO", "STREAM", "COLLECTION"] as const;

export const Contents: PlatformBlockComponent = ({
  name,
  platformId,
  queries,
  values,
}) => {
  const { t } = useTranslation();

  const viewOptions = viewValues.map((value) => ({
    value,
    label: t(`platformBlocks.view.${value}`),
  }));

  const previewModeOptions = previewModeValues.map((value) => ({
    value,
    label: t(`platformBlocks.previewMode.${value}`),
  }));

  const sortOptions = sortValues.map((value) => ({
    value,
    label: t(`platformBlocks.sort.${value}`),
  }));

  const statusOptions = statusValues.map((value) => ({
    value,
    label: t(`platformBlocks.streamStatus.${value}`),
  }));

  const typeOptions = typeValues.map((value) => ({
    value,
    label: t(`platformBlocks.contentType.${value}`),
  }));

  const props = values?.props as Record<string, unknown> | undefined;

  const type = props?.type as string | undefined;
  const sort = props?.sort as string | undefined;
  const showStatus = type === "STREAM" && (sort ?? "LATEST") === "LATEST";
  const showPreviewMode =
    type === "VIDEO" || type === "COLLECTION" || type === undefined;
  const showCollectionTypes = type === "COLLECTION";
  const showMoreUrl = ["CAROUSEL", "GRID", undefined].includes(
    props?.view as string | undefined
  );

  const collectionTypesQuery =
    (queries?.collectionTypes as DocumentNode | undefined) ?? NOOP_QUERY;
  const { data: collectionTypesData } = useQuery(collectionTypesQuery, {
    skip: !showCollectionTypes || !queries?.collectionTypes,
  });
  const collectionTypeOptions = (
    collectionTypesData?.collectionTypes ?? []
  ).map((ct: { id: string; title: string }) => ({
    label: ct.title,
    value: ct.id,
  }));

  return (
    <Paper sx={{ p: 2 }}>
      <BlockFormHeader blockType="CONTENTS" props={props} />
      <Grid container spacing={2}>
        <Grid size={12}>
          <TextField
            name={`${name}.title`}
            label={t("platformBlocks.fields.title")}
            type="text"
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <AutocompleteField
            name={`${name}.view`}
            label={t("platformBlocks.fields.view")}
            fullWidth
            options={viewOptions}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <AutocompleteField
            name={`${name}.type`}
            label={t("platformBlocks.fields.contentType")}
            fullWidth
            options={typeOptions}
          />
        </Grid>
        {showPreviewMode && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <AutocompleteField
              name={`${name}.previewMode`}
              label={t("platformBlocks.fields.previewMode")}
              fullWidth
              options={previewModeOptions}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12, sm: 4 }}>
          <ItemsPerRowField
            name={`${name}.itemsPerRow`}
            label={t("platformBlocks.fields.itemsPerRow")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            name={`${name}.limit`}
            label={t("platformBlocks.fields.limit")}
            type="number"
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <AutocompleteField
            name={`${name}.sort`}
            label={t("platformBlocks.fields.sort")}
            fullWidth
            options={sortOptions}
          />
        </Grid>
        {showStatus && (
          <Grid size={12}>
            <AutocompleteField
              name={`${name}.status`}
              label={t("platformBlocks.fields.streamStatus")}
              fullWidth
              options={statusOptions}
            />
          </Grid>
        )}
        <Grid size={12}>
          <AutocompleteField
            multiple
            name={`${name}.tagIds`}
            label={t("platformBlocks.fields.tagsAll")}
            fullWidth
            query={queries?.autocompleteTags}
            queryVariables={
              platformId
                ? {
                    filter: { platformId },
                  }
                : undefined
            }
          />
        </Grid>
        <Grid size={12}>
          <AutocompleteField
            multiple
            name={`${name}.anyTagIds`}
            label={t("platformBlocks.fields.tagsAny")}
            fullWidth
            query={queries?.autocompleteTags}
            queryVariables={
              platformId
                ? {
                    filter: { platformId },
                  }
                : undefined
            }
          />
        </Grid>
        {showCollectionTypes && (
          <Grid size={12}>
            <AutocompleteField
              multiple
              name={`${name}.collectionTypeIds`}
              label={t("platformBlocks.fields.collectionTypes")}
              fullWidth
              options={collectionTypeOptions}
            />
          </Grid>
        )}
        {showMoreUrl && (
          <Grid size={12}>
            <TextField
              name={`${name}.moreUrl`}
              label={t("platformBlocks.fields.moreUrl")}
              type="text"
              fullWidth
            />
          </Grid>
        )}
        {showMoreUrl && (
          <Grid size={12}>
            <TextField
              name={`${name}.moreText`}
              label={t("platformBlocks.fields.moreText")}
              type="text"
              fullWidth
            />
          </Grid>
        )}
        <Grid size={12}>
          <Switch
            name={`${name}.useCurrentTag`}
            label={t("platformBlocks.fields.useCurrentTag")}
            helperText={t("platformBlocks.fields.useCurrentTagHelper")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Switch
            name={`${name}.loadMore`}
            label={t("platformBlocks.fields.loadMoreOnScroll")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Switch
            name={`${name}.noMore`}
            label={t("platformBlocks.fields.hideShowAll")}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};
